// ── Height view ────────────────────────────────────────────────────────────────
// Height collection rendered as a phase of the main modal — the same card that
// morphs into the upload flow, so it reads as one shared element.

import { storeHeightIn } from '../height-dialog';
import type { ViewContext } from '../modal-types';

export function renderHeight(ctx: ViewContext): void {
  const { state, content, set } = ctx;

  const feet = state.heightFeet;
  const inch = state.heightInch;
  const menuOpen = state.heightMenuOpen;
  const page = state.heightMenuPage;
  const fmt = `${feet} ft ${inch} in`;

  const feetOpts: string[] = [];
  for (let f = 3; f <= 8; f++) {
    const active = f === feet ? ' ts-hopt-active' : '';
    const marker = f === feet ? '<span>✓</span>' : '';
    feetOpts.push(`<button class="ts-hopt${active}" type="button" data-feet="${f}"><span>${f} ft</span>${marker}</button>`);
  }

  const inchOpts: string[] = [];
  for (let i = 0; i <= 11; i++) {
    const active = i === inch ? ' ts-hopt-active' : '';
    const marker = i === inch ? '<span>✓</span>' : '';
    inchOpts.push(`<button class="ts-hopt${active}" type="button" data-inch="${i}"><span>${i} in</span>${marker}</button>`);
  }

  content.innerHTML = `
    ${ctx.headerHTML()}
    <div class="ts-mbody">
      <h3 class="ts-htitle">Your height is required for size recommendation</h3>
      <div class="ts-hpick">
        <button class="ts-hpicker" id="ts-hdlg-h" type="button" aria-haspopup="true" aria-expanded="${menuOpen}">
          <span id="ts-hdlg-h-label">${fmt}</span>
          <svg class="ts-hchev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </button>
        <div class="ts-hmenu" id="ts-hdlg-menu" ${menuOpen ? '' : 'hidden'}>
          <div class="ts-hpage ${page === 'ft' ? 'ts-hactive' : ''}" id="ts-hpage-ft">
            <div class="ts-hhead">Feet</div>
            <div class="ts-hlist" id="ts-hlist-ft">${feetOpts.join('')}</div>
          </div>
          <div class="ts-hpage ${page === 'in' ? 'ts-hactive' : ''}" id="ts-hpage-in">
            <div class="ts-hhead">
              <button class="ts-hback" id="ts-hback" type="button" aria-label="Back to feet">‹</button>
              <span>${feet} ft</span>
            </div>
            <div class="ts-hlist" id="ts-hlist-in">${inchOpts.join('')}</div>
          </div>
        </div>
      </div>
      <button class="ts-btn-primary" id="ts-hdlg-submit" type="button" style="margin-top:0">Continue</button>
    </div>
  `;

  content.querySelector('#ts-hdlg-h')?.addEventListener('click', () => {
    if (state.heightMenuOpen) {
      set({ heightMenuOpen: false });
    } else {
      set({ heightMenuOpen: true, heightMenuPage: 'ft' });
    }
  });

  content.querySelector('#ts-hback')?.addEventListener('click', () => {
    set({ heightMenuPage: 'ft' });
  });

  content.querySelectorAll<HTMLButtonElement>('[data-feet]').forEach((b) => {
    b.addEventListener('click', () => {
      set({ heightFeet: Number(b.dataset.feet), heightMenuPage: 'in' });
    });
  });

  content.querySelectorAll<HTMLButtonElement>('[data-inch]').forEach((b) => {
    b.addEventListener('click', () => {
      set({ heightInch: Number(b.dataset.inch), heightMenuOpen: false });
    });
  });

  content.querySelector('#ts-hdlg-submit')?.addEventListener('click', () => {
    const totalIn = state.heightFeet * 12 + state.heightInch;
    storeHeightIn(String(totalIn));
    const cm = Math.round(totalIn * 2.54);
    const resolve = state.heightResolve;
    const next = state.heightNext ?? 'upload';
    set({ phase: next, heightResolve: null, heightMenuOpen: false });
    resolve?.(cm);
  });

  // The dropdown would be clipped by the modal card's overflow — anchor it to
  // the viewport (fixed) just below the picker so it floats above the card.
  const menu = content.querySelector<HTMLElement>('#ts-hdlg-menu');
  const picker = content.querySelector<HTMLElement>('#ts-hdlg-h');
  if (menu && picker && state.heightMenuOpen) {
    const r = picker.getBoundingClientRect();
    menu.style.position = 'fixed';
    menu.style.top = `${r.bottom + 6}px`;
    menu.style.left = `${r.left}px`;
    menu.style.width = `${r.width}px`;
    menu.style.zIndex = '100001';
  }
}
